import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function NotFound() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith("ar");

  const title = isRTL ? "الصفحة غير موجودة" : "Page Not Found";
  const description = isRTL
    ? "الرابط الذي طلبته غير متوفر حاليًا. تأكد من الرابط أو ارجع للصفحة الرئيسية."
    : "The page you requested is unavailable. Check the URL or return to the homepage.";
  const pathLabel = isRTL ? "المسار المطلوب" : "Requested Path";
  const homeLabel = isRTL ? "العودة للرئيسية" : "Back to Home";
  const contactLabel = isRTL ? "تواصل معنا" : "Contact Us";

  return (
    <div className="relative min-h-screen overflow-hidden bg-stone-950 text-white">
      <motion.div
        className="pointer-events-none absolute -top-24 left-[-6rem] h-72 w-72 rounded-full bg-brand/40 blur-3xl"
        animate={{ x: [0, 40, 0], y: [0, 30, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="pointer-events-none absolute bottom-[-6rem] right-[-4rem] h-80 w-80 rounded-full bg-amber-300/25 blur-3xl"
        animate={{ x: [0, -35, 0], y: [0, -20, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.14),transparent_45%)]" />

      <main className="relative z-10 mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-12 md:px-8 md:py-16 lg:px-12">
        <div className="grid w-full grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="order-2 lg:order-1"
          >
            <p className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold tracking-wide text-white/80 md:text-sm">
              {pathLabel}
            </p>
            <h1 className="mt-5 text-4xl font-bold leading-tight text-white md:text-6xl">{title}</h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">{description}</p>

            <div className="mt-4 rounded-xl border border-white/15 bg-black/30 px-4 py-3">
              <p className="break-all font-mono text-sm text-white/90">{location.pathname}</p>
            </div>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/"
                className="inline-flex items-center justify-center rounded-full bg-brand px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-dark md:text-base"
              >
                {homeLabel}
              </Link>
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center rounded-full border border-white/30 bg-white/5 px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10 md:text-base"
              >
                {contactLabel}
              </Link>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="order-1 flex items-center justify-center lg:order-2"
          >
            <div className="relative w-full max-w-xl overflow-hidden rounded-3xl border border-white/15 bg-white/10 p-6 shadow-[0_20px_80px_rgba(0,0,0,0.45)] backdrop-blur-md md:p-10">
              <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(232,87,7,0.2),rgba(255,255,255,0.04))]" />
              <div className="relative text-center">
                <p className="text-[5rem] font-black leading-none tracking-tight text-transparent md:text-[8rem] bg-gradient-to-b from-white to-white/30 bg-clip-text">
                  404
                </p>
                <p className="mt-3 text-sm text-white/70 md:text-base">
                  {isRTL ? "الوجهة التي تبحث عنها اختفت من الرادار." : "The destination you are looking for has gone off radar."}
                </p>
              </div>
            </div>
          </motion.section>
        </div>
      </main>
    </div>
  );
}